import AccountBoxIcon from "@mui/icons-material/AccountBox";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import FolderIcon from "@mui/icons-material/Folder";
import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Fab,
  Paper,
} from "@mui/material";
import { mixpanel } from "common/analytics";
import {
  DashboardIcon,
  FarmIcon,
  InputsOutputsIcon,
  InsightsIcon,
  RecordsIcon,
} from "common/icons";
import { useTranslation } from "common/locales";
import { useIsMobile, useUserType } from "common/utils";
import { useFarmerDashboards } from "model";
import { useCurrentFarm } from "model/useFarmQueries";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import RadialMenu from "./RadialMenu";
import { useAddNewEventDialogs } from "./useAddNewEventDialogs";

import { isFarmTabRoute, navigationConfig } from "app/routes";
import AccountTreeIconConfigurable from "common/icons/AccountTreeIconConfigurable";
import ProfileIcon from "common/icons/ProfileIcon";
import ProgrammesIcon from "common/icons/ProgrammesIcon";

/**
 * Bottom tab navigation component for mobile.
 * Shows different tabs based on user type (farmer vs corporate).
 */
const BottomTabNavigation = () => {
  const [isRadialMenuOpen, setIsRadialMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const userType = useUserType();
  const { data: currentFarm } = useCurrentFarm();
  const currentFarmId = currentFarm?.guid;
  const { data: farmerDashboards = [] } = useFarmerDashboards();

  const { handleEventTypeClick, dialogs, isAnyDialogOpen } =
    useAddNewEventDialogs();

  // Check if add cover dialog is open (when on /add-covers route)
  const isAddCoverDialogOpen = location.pathname.startsWith("/add-covers");

  // Determine which cover flow step we're on
  const coverFlowStep = isAddCoverDialogOpen
    ? location.pathname.includes("/enter-time")
      ? "enter-time"
      : location.pathname.includes("/enter-covers")
      ? "enter-covers"
      : location.pathname.includes("/enter-reasons")
      ? "enter-reasons"
      : location.pathname.includes("/confirm-covers")
      ? "confirm-covers"
      : null
    : null;

  // Check if any dialog or the add cover dialog is open
  const isButtonDisabled = isAnyDialogOpen || isAddCoverDialogOpen;

  // Check if on a public route (login, signup, etc.)
  const publicRoutes = [
    "/login",
    "/signup",
    "/signup-intro",
    "/login/password-forgot",
    "/login/password-reset",
  ];
  const isPublicRoute = publicRoutes.some(
    (route) =>
      location.pathname === route || location.pathname.startsWith(route + "/")
  );

  // Don't show on desktop, public routes, or if no current farm (for farmers)
  if (!isMobile || isPublicRoute || (userType === "farmer" && !currentFarmId)) {
    return null;
  }

  const handleAddClick = () => {
    if (isButtonDisabled) return;
    setIsRadialMenuOpen(!isRadialMenuOpen);
  };

  const handleRadialMenuClose = () => {
    setIsRadialMenuOpen(false);
  };

  const handleEventMenuItemClick = (eventType: string) => {
    handleRadialMenuClose();
    handleEventTypeClick(eventType);
  };

  // Icon mapping for navigation config
  const iconMap: Record<string, React.ReactNode> = {
    FarmIcon: <FarmIcon />,
    InputsOutputsIcon: <InputsOutputsIcon />,
    RecordsIcon: <RecordsIcon />,
    InsightsIcon: <InsightsIcon />,
    AddButtonIcon: null, // FAB is rendered separately
    ProgrammesIcon: <ProgrammesIcon />,
    DashboardIcon: <DashboardIcon />,
    AccountTreeIconConfigurable: <AccountTreeIconConfigurable />,
    FolderIcon: <FolderIcon />,
    AccountBoxIcon: <AccountBoxIcon />,
    ProfileIcon: <ProfileIcon />,
  };

  // Build tabs from navigation config
  const navConfig = navigationConfig[userType];
  const tabs = navConfig.tabs.map((tab) => ({
    label: tab.isAddButton ? "" : t(tab.labelKey),
    icon: iconMap[tab.iconName] || null,
    value: tab.isAddButton ? "add" : tab.getPath(currentFarmId),
    analyticsEvent: tab.analyticsEvent,
    isAddButton: tab.isAddButton,
  }));

  const defaultTabValue = navConfig.getDefaultRoute(currentFarmId);

  // Get current value based on location
  const getCurrentValue = () => {
    // If any dialog is open, don't select any tab
    if (isButtonDisabled || isRadialMenuOpen) {
      return false;
    }

    const currentPath = location.pathname;

    // For farmer users, keep farm tab selected for farm-related views
    if (userType === "farmer" && isFarmTabRoute(currentPath)) {
      return `/farm/${currentFarmId}`;
    }

    // Handle farm insights tab selection
    if (farmerDashboards.length > 0 && currentPath === "/farm-insights") {
      return "/farm-insights";
    }

    return (
      tabs.find((tab) => tab.value === currentPath)?.value ?? defaultTabValue
    );
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    handleRadialMenuClose();

    if (newValue === "add") {
      return; // Handled by the wrapper click
    }

    const tab = tabs.find((tab) => tab.value === newValue);
    if (tab) {
      mixpanel.track(tab.analyticsEvent);
      navigate(newValue);
    }
  };

  // Check if this user type has an add button
  const hasAddButton = navConfig.tabs.some((tab) => tab.isAddButton);

  // Click handlers for split FABs - handle cover flow navigation or trigger Cancel/Save buttons
  const handleCancelClick = () => {
    // Handle cover flow navigation
    if (coverFlowStep) {
      switch (coverFlowStep) {
        case "enter-time":
          // Cancel - dispatch event so component can show dialog if needed
          window.dispatchEvent(new CustomEvent("cover-flow-cancel"));
          return;
        case "enter-covers":
        case "enter-reasons":
        case "confirm-covers":
          // Back - dispatch event so component can show dialog if needed, or go back
          window.dispatchEvent(new CustomEvent("cover-flow-back"));
          return;
      }
    }

    // Find the Cancel button in the dialog by text content
    const buttons = document.querySelectorAll("button");
    for (const btn of buttons) {
      if (btn.textContent?.trim() === "Cancel") {
        btn.click();
        return;
      }
    }
  };

  const handleSaveClick = () => {
    // Handle cover flow navigation
    if (coverFlowStep) {
      switch (coverFlowStep) {
        case "enter-time":
          navigate("/add-covers/enter-covers");
          return;
        case "enter-covers":
          navigate("/add-covers/enter-reasons");
          return;
        case "enter-reasons":
          navigate("/add-covers/confirm-covers");
          return;
        case "confirm-covers": {
          // Trigger the actual Save button
          const buttons = document.querySelectorAll("button");
          for (const btn of buttons) {
            if (btn.textContent?.trim() === "Save") {
              btn.click();
              return;
            }
          }
          return;
        }
      }
    }

    // Find the Save button in the dialog by text content
    const buttons = document.querySelectorAll("button");
    for (const btn of buttons) {
      if (btn.textContent?.trim() === "Save") {
        btn.click();
        return;
      }
    }
  };

  // Determine what icons to show for the FABs based on context
  const getLeftFabIcon = () => {
    if (coverFlowStep && coverFlowStep !== "enter-time") {
      return <ChevronLeftIcon sx={{ fontSize: 24 }} />;
    }
    return <CloseIcon sx={{ fontSize: 24 }} />;
  };

  const getRightFabIcon = () => {
    if (coverFlowStep && coverFlowStep !== "confirm-covers") {
      return <ChevronRightIcon sx={{ fontSize: 24 }} />;
    }
    return <CheckIcon sx={{ fontSize: 24 }} />;
  };

  return (
    <>
      {/* Floating Action Buttons */}
      {hasAddButton && (
        <Box
          sx={{
            position: "fixed",
            bottom: "calc(25px + env(safe-area-inset-bottom))",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Cancel FAB (left) */}
          <Fab
            onClick={handleCancelClick}
            size="medium"
            sx={{
              position: "absolute",
              backgroundColor: "#D32F2F",
              color: "white",
              width: 48,
              height: 48,
              opacity: isButtonDisabled ? 1 : 0,
              transform: isButtonDisabled
                ? "translateX(-40px) scale(1)"
                : "translateX(0) scale(0)",
              transition: "transform 0.35s ease, opacity 0.35s ease",
              pointerEvents: isButtonDisabled ? "auto" : "none",
              "&:hover": {
                backgroundColor: "#B71C1C",
              },
            }}
          >
            {getLeftFabIcon()}
          </Fab>

          {/* Save FAB (right) */}
          <Fab
            onClick={handleSaveClick}
            size="medium"
            sx={{
              position: "absolute",
              backgroundColor: "#2E7D32",
              color: "white",
              width: 48,
              height: 48,
              opacity: isButtonDisabled ? 1 : 0,
              transform: isButtonDisabled
                ? "translateX(40px) scale(1)"
                : "translateX(0) scale(0)",
              transition: "transform 0.35s ease, opacity 0.35s ease",
              pointerEvents: isButtonDisabled ? "auto" : "none",
              "&:hover": {
                backgroundColor: "#1B5E20",
              },
            }}
          >
            {getRightFabIcon()}
          </Fab>

          {/* Main Add FAB (center) */}
          <Fab
            onClick={handleAddClick}
            disabled={isButtonDisabled}
            sx={{
              backgroundColor: isRadialMenuOpen ? "#2E7831" : "#1C4321",
              color: "white",
              width: 62,
              height: 62,
              opacity: isButtonDisabled ? 0 : 1,
              transform: isRadialMenuOpen
                ? "scale(1.05)"
                : isButtonDisabled
                ? "scale(0)"
                : "scale(1)",
              transition:
                "transform 0.35s ease, background-color 0.35s ease, opacity 0.35s ease",
              pointerEvents: isButtonDisabled ? "none" : "auto",
              "&:hover": {
                backgroundColor: isRadialMenuOpen ? "#2E7831" : "#153318",
              },
              "&.Mui-disabled": {
                backgroundColor: "#1C4321",
                color: "white",
                opacity: 0,
              },
            }}
          >
            <AddIcon
              sx={{
                fontSize: 32,
                transform: isRadialMenuOpen ? "rotate(135deg)" : "rotate(0deg)",
                transition: "transform 0.35s ease",
              }}
            />
          </Fab>
        </Box>
      )}
      <Paper
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          paddingBottom: "env(safe-area-inset-bottom)",
          backgroundColor: "white",
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
        }}
        elevation={8}
      >
        <BottomNavigation
          value={getCurrentValue()}
          onChange={handleChange}
          showLabels
          sx={{
            backgroundColor: "white",
            height: "auto",
            minHeight: "70px",
            borderTopLeftRadius: "16px",
            borderTopRightRadius: "16px",
            "& .MuiBottomNavigationAction-root": {
              color: "#9E9E9E",
              "&.Mui-selected": {
                color: "#1C4321",
                "& .MuiBottomNavigationAction-label": {
                  fontWeight: 600,
                },
                "& .MuiSvgIcon-root": {
                  strokeWidth: 2,
                },
              },
            },
            "& .MuiBottomNavigationAction-label": {
              fontSize: "0.75rem",
              lineHeight: 1.2,
              textAlign: "center",
              whiteSpace: "normal",
              marginTop: "0.25rem",
              fontWeight: 400,
              "&.Mui-selected": {
                fontSize: "0.75rem",
              },
            },
          }}
        >
          {tabs.map((tab) =>
            tab.isAddButton ? (
              // Empty spacer for FAB position
              <BottomNavigationAction
                key={tab.value}
                label=""
                icon={null}
                value={tab.value}
                disabled
                sx={{
                  minWidth: 72,
                  padding: "8px 2px",
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />
            ) : (
              <BottomNavigationAction
                key={tab.value}
                label={tab.label}
                icon={tab.icon}
                value={tab.value}
                sx={{
                  minWidth: 0,
                  padding: "0.5rem 0.125rem",
                  "& .MuiSvgIcon-root": {
                    fontSize: "1.65rem",
                    color: "inherit",
                  },
                }}
              />
            )
          )}
        </BottomNavigation>
      </Paper>
      <RadialMenu
        open={isRadialMenuOpen}
        onClose={handleRadialMenuClose}
        onEventTypeClick={handleEventMenuItemClick}
      />
      {dialogs}
    </>
  );
};

export default BottomTabNavigation;
